import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, forwardRef, useContext, useRef, useMemo } from 'react';
import { Icon24Close } from '@figma/fpl-icons/icon-24-close';
import { Icon24NavigateBack } from '@figma/fpl-icons/icon-24-navigate-back';
import { useColorTheme } from '@figma/fpl-themes';
import clsx from 'clsx';
import '../buttons/button.js';
import '../buttons/dialog-trigger-button/dialog-trigger-button.js';
import { IconButton } from '../buttons/icon-button/icon-button.js';
import '../buttons/toggle-button/toggle-button.js';
import { Label } from '../dialog-primitive/dialog-primitive.js';
import { ForceActionOnClickContext } from '../hooks/use-clickable.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { ScrollContainer } from '../scroll-container/scroll-container.js';
import { useString } from '../settings/settings.strings.js';
import { TabStrip as TabStrip$1 } from '../tabs/tab-strip.js';
import { HAS_HOUDINI } from '../utils/css.js';
import { resolvePadding } from '../utils/padding.js';
import { useSectionForceActionOnClick, useClose, useHasCustomClose, useRegisterCustomClose } from './dialog-common.context.js';
import { customContents, header, customHeader, body, bodyNoScroll, bodyScrollContainer, bodyInner, customFooter, close, closeTheme, actionStrip, contentsLayout, hasCustomClose, hiddenTitle, tabStripContent, tabStripScroll, tabStripRoot, title, customClose, styledFooter, formContents, arrow } from './dialog-common.css.js';

/** Context for sharing refs from `Contents` to its child components. */
const DialogChildRefsContext = createContext(null);
/**
 * The wrapper that gives the dialog / modal / window its rounded corners and drop shadow,
 * as well as sets up the grid layout for its children.
 *
 * The only children that are allowed as direct children of `Contents` are:
 *
 * - {@link Header|Dialog.Header}
 * - {@link Body|Dialog.Body}
 * - {@link Footer|Dialog.Footer}
 * - {@link Sidebar|Dialog.Sidebar}
 *
 * It is fine to abstract these into other components, as long as they are not
 * wrapped inside of elements like divs etc.
 *
 * @note If your modal falls outside of the norm, you can use `CustomContents`,
 * which does not set up a grid and is safe to use other elements inside of.
 *
 * @compose { "ancestor": ["Form", "..."], "oneOf": "contents", "multi": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/dialog/
 */
function Contents({ className, layout = 'sidebar-full-height', ...props }) {
    const contentsRef = useRef(null);
    const bodyRef = useRef(null);
    const scrollRef = useRef(null);
    const childRefs = useMemo(() => ({ contentsRef, bodyRef, scrollRef }), []);
    const hasCustomClose$1 = useHasCustomClose();
    return (jsx(DialogChildRefsContext.Provider, { value: childRefs, children: jsx("div", { "data-fpl-component": true, ...props, ref: contentsRef, className: clsx(className, contentsLayout[layout], {
                [hasCustomClose]: hasCustomClose$1,
            }) }) }));
}
Contents.displayName = 'Dialog.Contents';
/**
 * The wrapper that gives the modal/dialog its rounded corners and drop shadow,
 * as well as sets up the layout for its children.
 *
 * @compose { "ancestor": ["Form", "..."], "oneOf": "contents", "multi": true }
 * @link https://fpl.figma.design/components/dialog/
 */
const CustomContents = forwardRef(({ className, ...props }, ref) => (jsx("div", { "data-fpl-component": "", ...props, ref: ref, className: clsx(customContents, className) })));
CustomContents.displayName = 'Dialog.CustomContents';
/**
 * Identical to Dialog.Contents, except renders a <form> element. When using this component,
 * make sure to have one `<button type="submit">` within the dialog to trigger the form submission.
 * The button should not have its own `onClick` handler but instead rely on the `onSubmit` of this
 * form.
 *
 * @compose { "oneOf": "contents", "multi": true, "strictChildren": true }
 */
function FormContents({ children, onSubmit, ...props }) {
    return (jsx("form", { "data-fpl-component": "", ...props, className: formContents, onSubmit: (e) => {
            onSubmit(e);
            // call `e.preventDefault` after onSubmit, which gives wrapper components the ability to
            // use `e.defaultPrevented` for their own purposes
            e.preventDefault();
        }, children: jsx(Contents, { children: children }) }));
}
FormContents.displayName = 'Dialog.FormContents';
/**
 * Identical to Dialog.Contents, except renders a <form> element. When using this component,
 * make sure to have one `<button type="submit">` within the dialog to trigger the form submission.
 * The button should not have its own `onClick` handler but instead rely on the `onSubmit` of this
 * form.
 *
 * This component accepts `className` and `style` props to customize the dialog contents.
 *
 * @compose { "oneOf": "contents", "multi": true }
 */
function CustomFormContents({ children, className, style, onSubmit, ...props }) {
    return (jsx("form", { "data-fpl-component": "", ...props, className: formContents, onSubmit: (e) => {
            onSubmit(e);
            // call `e.preventDefault` after onSubmit, which gives wrapper components the ability to
            // use `e.defaultPrevented` for their own purposes
            e.preventDefault();
        }, children: jsx(CustomContents, { className: className, style: style, children: children }) }));
}
CustomFormContents.displayName = 'Dialog.CustomFormContents';
/**
 * Render a pointed arrow off an edge of the dialog.
 *
 * @note This must be a direct child of `Contents` or `CustomContents`.
 * @compose { "parent": ["Dialog.Contents", "Dialog.CustomContents"] }
 * @link https://fpl.figma.design/components/dialog/
 */
function Arrow({ position, offset }) {
    const theme = useColorTheme();
    return (jsxs("svg", { className: arrow[position], width: "12", height: "6", viewBox: "0 0 12 6", fill: "none", style: {
            [position === 'top' || position === 'bottom' ? 'left' : 'top']: offset,
        }, children: [jsx("path", { d: "M6 0L0 6L12 6Z", fill: "var(--color-bg)" }), theme.mode === 'dark' && jsx("path", { d: "M0 6L6 0L12 6", stroke: "var(--color-border)" })] }));
}
Arrow.displayName = 'Dialog.Arrow';
/**
 * The header section, separated from the body by a bottom border. Should always contain
 * either a `{@link Title|Dialog.Title}` or `{@link HiddenTitle|Dialog.HiddenTitle}`.
 *
 * @note In rare cases, a `Header` can be omitted. The upper-right `X` button will still be
 * rendered, but a `HiddenTitle` should be provided for screen readers.
 *
 * @note `Header` **must** be a direct child of `{@link Contents|Dialog.Contents}`.
 *
 * ```
 * ┌────────────┐
 * │   Header   │
 * ├────────────┤
 * │            │
 * ├────────────┤
 * └────────────┘
 * ```
 *
 * @compose { "parent": ["Dialog.Contents"], "oneOf": "header" }
 * @link https://fpl.figma.design/components/dialog/
 */
const Header = forwardRef(({ children, ...props }, ref) => (jsx("div", { "data-fpl-component": "", className: header, "data-fpl-header": true, ...props, ref: ref, children: jsx(ForceActionOnClickContext.Provider, { value: useSectionForceActionOnClick('header'), children: children }) })));
Header.displayName = 'Dialog.Header';
/**
 * The header section of modal content, offering more flexibility than the standard `{@link Header}`
 * component. This component sets up a bottom border and places the header at the top of the dialog,
 * but does not otherwise set up any styling. Accepts `className` and `style` props.
 *
 * Should always contain `{@link Title|Dialog.Title}` or `{@link HiddenTitle|Dialog.HiddenTitle}`.
 *
 * @note {@link ActionStrip} will not work out-of-the-box within this component.
 * @note `CustomHeader` **must** be a direct child of `{@link Contents|Dialog.Contents}`.
 *
 * ```
 * ┌────────────┐
 * │CustomHeader│
 * ├────────────┤
 * │            │
 * ├────────────┤
 * └────────────┘
 * ```
 *
 * @compose { "parent": ["Dialog.Contents"], "oneOf": "header" }
 */
const CustomHeader = forwardRef(({ className, ...props }, ref) => (jsx("div", { "data-fpl-component": "", ...props, className: clsx(customHeader, className), ref: ref })));
CustomHeader.displayName = 'Dialog.CustomHeader';
/**
 * Close button for all dialogs, windows, and modals
 * The close button is included in all components that use the dialog-common module,
 * so it's not exported from @figma/fpl-components
 */
function CloseButton(props) {
    const close$1 = useClose();
    const closeLabel = useString('close');
    return (jsx("div", { "data-fpl-component": "", "data-slot": "close", className: clsx(close, props.overrideColor && closeTheme[props.overrideColor]), children: jsx(IconButton, { onClick: () => close$1({ source: 'button' }), "aria-label": closeLabel, children: jsx(Icon24Close, {}) }) }));
}
CloseButton.displayName = 'Dialog.CloseButton';
/**
 * Custom close button for all dialogs, windows, and modals. If you need more customization
 * than the CloseButton that gets placed inside of dialogs by default, you can use this component
 * to completely control the positioning of the close button. Ideally this lives inside of the
 * header still, so it comes in a natural tab order.
 *
 * This should really only be used in custom situations where the default CloseButton would
 * otherwise appear to be uncentered, e.g. when using a large input in the header.
 *
 */
function CustomCloseButton({ className, ...props }) {
    const close = useClose();
    const closeLabel = useString('close');
    const register = useRegisterCustomClose();
    return (jsx("div", { "data-fpl-component": "", ...props, ref: register, "data-slot": "close", className: clsx(customClose, className), children: jsx(IconButton, { onClick: () => close({ source: 'button' }), "aria-label": closeLabel, children: jsx(Icon24Close, {}) }) }));
}
CustomCloseButton.displayName = 'Dialog.CustomCloseButton';
/**
 * Title for the modal/dialog/window
 *
 * @link https://fpl.figma.design/components/dialog/
 */
function Title(props) {
    return jsx(Label, { "data-fpl-component": "", className: title, ...props });
}
Title.displayName = 'Dialog.Title';
/**
 * If a `{@link Title|Dialog.Title}` is not included, a `HiddenTitle` should be used to act
 * as the dialog / window / modal's aria-label.
 *
 * @link https://fpl.figma.design/components/dialog/
 */
function HiddenTitle(props) {
    return jsx(Label, { "data-fpl-component": "", className: hiddenTitle, ...props });
}
HiddenTitle.displayName = 'Dialog.HiddenTitle';
/**
 * A back button to be placed in the dialog's header. This should be the
 * first element in the header, typically followed by a `{@link Title|Dialog.Title}`.
 *
 * @link https://fpl.figma.design/components/dialog/
 */
function BackButton({ onClick }) {
    const label = useString('back');
    return (jsx(IconButton, { "data-fpl-component": "", onClick: onClick, "aria-label": label, children: jsx(Icon24NavigateBack, {}) }));
}
BackButton.displayName = 'Dialog.BackButton';
/**
 * Tab strips allow users to navigate between two or more related views.
 *
 * This should be preferred over using `{@link TabsTabBase|Tabs.TabStrip}` directly within a dialog
 * Header because it sets up horizontal scrolling and ensures styling compatible with dialogs.
 *
 * @compose { "parent": ["Dialog.Header"], "wraps": ["Tabs.TabStrip"] }
 * @see https://fpl.figma.design/components/tabs/
 */
function TabStrip(props) {
    return (jsx(ScrollContainer, { "data-fpl-component": "", scroll: "x", fill: true, styles: {
            root: tabStripRoot,
            scroll: HAS_HOUDINI ? tabStripScroll.houdini : tabStripScroll.noHoudini,
            content: tabStripContent,
        }, children: jsx(TabStrip$1, { ...props }) }));
}
TabStrip.displayName = 'Dialog.TabStrip';
/**
 * The main content of the modal. Should come between the `{@link Header|Dialog.Header}` and `{@link Footer|Dialog.Footer}`.
 *
 * @note `<Body>` **must** be a direct child of `<{@link Contents|Dialog.Contents}>`.
 *
 * @note The body is set up to automatically allow scrolling if the content is too large to fit
 * without the modal growing to 80% of the viewport height.
 *
 * ```
 * ┌────────────┐
 * ├────────────┤
 * │    Body    │
 * ├────────────┤
 * └────────────┘
 * ```
 *
 * @compose { "parent": ["Dialog.Contents"] }
 * @link https://fpl.figma.design/components/dialog/
 */
const Body = forwardRef(({ children, className, style, padding, scrolling, ...props }, refIn) => {
    const childRefs = useContext(DialogChildRefsContext);
    const ref = useMergeRefs(refIn, childRefs?.bodyRef);
    const forceActionOnClickBody = useSectionForceActionOnClick('body');
    // A wrapping Window can release `ForceActionOnClickContext` for the body (e.g. when it only
    // drags from its header, the body is never a drag surface, so `actionOnPointerDown` is safe).
    return (jsx(ForceActionOnClickContext.Provider, { value: forceActionOnClickBody, children: scrolling === 'manual' ? (jsx("div", { "data-fpl-component": "", ...props, ref: ref, className: clsx(body, bodyNoScroll, className), style: { ...style, ...resolvePadding(padding) }, children: children })) : (jsx(ScrollContainer, { "data-fpl-component": "", ...props, ref: ref, fill: true, styles: {
                root: bodyScrollContainer,
                content: {
                    className: clsx(bodyInner, className),
                    style: { ...resolvePadding(padding), ...style },
                },
            }, children: children })) }));
});
Body.displayName = 'Dialog.Body';
/**
 * The bottom section of modal content.
 * Additional text may be displayed here, as well as a `{@link ActionStrip|Dialog.ActionStrip}` with buttons.
 *
 * @note `Footer` **must** be a direct child of `{@link Contents|Dialog.Contents}`.
 *
 * @note If the body of the modal scrolls, the footer will automatically
 *   be separated from the body with a top border.
 *
 * ```
 * ┌────────────┐
 * ├────────────┤
 * │            │
 * ├────────────┤
 * │   Footer   │
 * └────────────┘
 * ```
 *
 * @compose { "parent": ["Dialog.Contents"], "oneOf": "footer" }
 * @link https://fpl.figma.design/components/dialog/
 */
function Footer({ children }) {
    return (jsx("div", { "data-fpl-component": "", className: styledFooter, children: jsx(ForceActionOnClickContext.Provider, { value: useSectionForceActionOnClick('footer'), children: children }) }));
}
Footer.displayName = 'Dialog.Footer';
/**
 * The bottom section of modal content, offering more flexibility than the standard `{@link Footer}`
 * component. This component sets up a top border and places the footer at the bottom of the dialog,
 * but does not otherwise set up any styling. Accepts `className` and `style` props.
 *
 * @note {@link ActionStrip} will not work out-of-the-box within this component.
 * @note `CustomFooter` **must** be a direct child of `{@link Contents|Dialog.Contents}`.
 *
 * ```
 * ┌──────────────┐
 * ├──────────────┤
 * │              │
 * ├──────────────┤
 * │ CustomFooter │
 * └──────────────┘
 * ```
 *
 * @compose { "parent": ["Dialog.Contents"], "oneOf": "footer" }
 */
const CustomFooter = forwardRef(({ className, ...props }, ref) => (jsx("div", { "data-fpl-component": "", ...props, className: clsx(customFooter, className), ref: ref })));
CustomFooter.displayName = 'Dialog.CustomFooter';
/**
 * A group of buttons in the header or footer
 * In the header, the action strip will always align correctly with the X button
 *
 * @compose { "ancestor": ["Dialog.CustomFooter", "Dialog.CustomHeader", "Dialog.Footer", "Dialog.Header"] }
 * @link {https://fpl.figma.design/components/dialog/}
 */
function ActionStrip({ children }) {
    return (jsx("div", { "data-fpl-component": "", className: actionStrip, children: children }));
}
ActionStrip.displayName = 'Dialog.ActionStrip';

export { ActionStrip, Arrow, BackButton, Body, CloseButton, Contents, CustomCloseButton, CustomContents, CustomFooter, CustomFormContents, CustomHeader, DialogChildRefsContext, Footer, FormContents, Header, HiddenTitle, TabStrip, Title };
//# sourceMappingURL=dialog-common.js.map
